import { homeMetadata } from './seo';

export type PublicLocale = 'sv' | 'en';

export type PublicRouteId =
  | 'home'
  | 'customerCase'
  | 'process'
  | 'buildingPass'
  | 'services'
  | 'contact'
  | 'forManagers'
  | 'technology'
  | 'about'
  | 'documentation'
  | 'insights'
  | 'expensiveFacadeDamage'
  | 'newProperty'
  | 'facadeBudget'
  | 'exampleReport'
  | 'privacy'
  | 'terms';

interface PublicRouteDefinition {
  paths: Record<PublicLocale, string>;
  titles: Record<PublicLocale, string>;
  descriptions?: Record<PublicLocale, string>;
}

export const publicRouteDefinitions: Record<PublicRouteId, PublicRouteDefinition> = {
  home: {
    paths: { sv: '/', en: '/en' },
    titles: { sv: homeMetadata.sv.title, en: homeMetadata.en.title },
  },
  customerCase: {
    paths: { sv: '/kundcase', en: '/en/customer-case' },
    titles: { sv: 'Kundcase', en: 'Customer case' },
  },
  process: {
    paths: { sv: '/sa-fungerar-det', en: '/en/how-it-works' },
    titles: { sv: 'Så fungerar det', en: 'How it works' },
    descriptions: {
      sv: 'Se hur KlaraViews drönarinspektion går till, från första fråga och avgränsning till bilder, granskade observationer och underlag för nästa beslut.',
      en: 'See how a KlaraView drone inspection works, from your first question and agreed scope to images, reviewed observations and documentation for your next decision.',
    },
  },
  buildingPass: {
    paths: { sv: '/byggnadspasset', en: '/en/building-pass' },
    titles: { sv: 'Byggnadspasset', en: 'Building pass' },
    descriptions: {
      sv: 'Utforska KlaraViews illustrativa byggnadspass. Följ en observation från markerad plats till foto och möjligt nästa steg, med information samlad per fastighet.',
      en: "Explore KlaraView's illustrative Building Pass. Follow an observation from its marked location to a photo and possible next step, with information kept together.",
    },
  },
  services: {
    paths: { sv: '/tjanster', en: '/en/services' },
    titles: { sv: 'Tjänster', en: 'Services' },
    descriptions: {
      sv: 'Jämför Scan och Expert för fasadinspektion med drönare och AI-stöd: teamgranskad dokumentation, med oberoende ingenjörsgranskning i Expert enligt avtal.',
      en: 'Compare Scan and Expert for drone façade inspection with AI support: team-reviewed documentation, with independent engineering review in Expert within the agreed scope.',
    },
  },
  contact: {
    paths: { sv: '/kontakt', en: '/en/contact' },
    titles: { sv: 'Kontakta oss', en: 'Contact us' },
    descriptions: {
      sv: 'Ta nästa steg med KlaraView. Berätta om fastigheten och vad ni behöver förstå, så diskuterar vi förutsättningar och omfattning för ett möjligt uppdrag.',
      en: 'Take the next step with KlaraView. Tell us about your property and what you need to understand so we can discuss requirements and scope for a possible assignment.',
    },
  },
  forManagers: {
    paths: { sv: '/for-er', en: '/en/for-property-managers' },
    titles: { sv: 'För er som förvaltar', en: 'For property managers' },
    descriptions: {
      sv: 'Få ett tydligare underlag för fastighetsförvaltning. Se hur bilder och observationer kan stödja dialog, underhållsplanering och beslut för ägare och styrelser.',
      en: 'Get clearer documentation for property management. See how images and observations can support discussions, maintenance planning and decisions for owners and boards.',
    },
  },
  technology: {
    paths: { sv: '/teknik', en: '/en/technology' },
    titles: { sv: 'Teknik och mänsklig bedömning', en: 'Technology and professional assessment' },
    descriptions: {
      sv: 'Förstå hur drönarbilder, AI-stöd och mänsklig granskning samverkar i KlaraView. Se vad observationerna kan visa och vad som behöver undersökas närmare.',
      en: 'Discover how drone imagery, AI support and human review work together at KlaraView. Understand what observations show and what needs closer investigation.',
    },
  },
  about: {
    paths: { sv: '/om-oss', en: '/en/about' },
    titles: { sv: 'Om KlaraView', en: 'About KlaraView' },
    descriptions: {
      sv: 'Möt människorna bakom KlaraView. Läs om vår drivkraft: att förena teknik och affär med bättre underlag för dem som tar ansvar för byggnader.',
      en: 'Meet the people behind KlaraView. Discover why we bring technology and business together to give those responsible for buildings better information to work with.',
    },
  },
  documentation: {
    paths: { sv: '/dokumentation', en: '/en/documentation' },
    titles: { sv: 'Dokumentation och dataskydd', en: 'Documentation and data protection' },
    descriptions: {
      sv: 'Gör byggnadens information lättare att använda. Läs hur KlaraView samlar bilder, observationer och rapporter samt hanterar åtkomst, delning och lagring.',
      en: 'Make building information easier to use. Learn how KlaraView brings images, observations and reports together and handles access, sharing and retention.',
    },
  },
  insights: {
    paths: { sv: '/insikter', en: '/en/insights' },
    titles: { sv: 'Insikter', en: 'Insights' },
    descriptions: {
      sv: 'Få nya perspektiv på fasader, underhåll och fastighetsdata. Läs KlaraViews Insikter om tidig upptäckt, byggnadshistorik och bättre underlag för beslut.',
      en: 'Gain new perspectives on façades, maintenance and property data. Read KlaraView Insights on early discovery, building history and better information for decisions.',
    },
  },
  expensiveFacadeDamage: {
    paths: {
      sv: '/insikter/den-dyraste-fasadskadan',
      en: '/en/insights/the-most-expensive-facade-damage',
    },
    titles: {
      sv: 'Den dyraste fasadskadan är den du ännu inte känner till',
      en: 'The most expensive façade damage is the damage you do not know about yet',
    },
  },
  newProperty: {
    paths: {
      sv: '/insikter/den-ar-ju-ny',
      en: '/en/insights/it-is-new',
    },
    titles: {
      sv: '”Den är ju ny.” Fyra ord som kan bli väldigt dyra.',
      en: '“It is brand new.” Four words that can prove very costly.',
    },
  },
  facadeBudget: {
    paths: {
      sv: '/insikter/fasaddata-underhall-budget',
      en: '/en/insights/facade-data-maintenance-budget',
    },
    titles: {
      sv: 'Vad vet din fasad om nästa års budget?',
      en: 'What does your façade know about next year’s budget?',
    },
  },
  exampleReport: {
    paths: { sv: '/exempelrapport', en: '/en/example-report' },
    titles: { sv: 'Exempelrapport', en: 'Sample report' },
  },
  privacy: {
    paths: { sv: '/integritet', en: '/en/privacy' },
    titles: { sv: 'Integritetspolicy', en: 'Privacy policy' },
    descriptions: {
      sv: 'Läs hur KlaraView behandlar personuppgifter, för vilka ändamål och vilka rättigheter du har. Här finns information om dataskydd och hur du kontaktar oss.',
      en: 'Read how KlaraView processes personal data, the purposes of processing and your rights. Find information about data protection and how to contact us.',
    },
  },
  terms: {
    paths: { sv: '/villkor', en: '/en/terms' },
    titles: { sv: 'Användarvillkor', en: 'Terms of service' },
    descriptions: {
      sv: 'Läs KlaraViews användarvillkor om uppdragets omfattning, dokumentation, ansvar och användning av leveransen. Se vad som regleras i uppdragsavtalet.',
      en: "Read KlaraView's terms of service on assignment scope, documentation, responsibility and use of deliverables. See what is set out in the assignment agreement.",
    },
  },
};

export const publicRoutes = Object.values(publicRouteDefinitions).reduce<Record<string, string>>((routes, route) => {
  routes[route.paths.sv] = route.titles.sv;
  routes[route.paths.en] = route.titles.en;
  return routes;
}, {});

const routeEntries = (Object.keys(publicRouteDefinitions) as PublicRouteId[]).flatMap(routeId => {
  const route = publicRouteDefinitions[routeId];
  return (['sv', 'en'] as PublicLocale[]).map(locale => ({
    locale,
    path: route.paths[locale],
    routeId,
    title: route.titles[locale],
    description: route.descriptions?.[locale],
  }));
});

export function normalizePath(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

export function getPublicRoute(path: string) {
  const normalized = normalizePath(path);
  return routeEntries.find(route => route.path === normalized);
}

export function isPublicPath(path: string): boolean {
  return Boolean(getPublicRoute(path));
}

export function publicPath(routeId: PublicRouteId, locale: PublicLocale): string {
  return publicRouteDefinitions[routeId].paths[locale];
}

export function equivalentPublicUrl(href: string, locale: PublicLocale): string {
  const source = new URL(href, window.location.origin);
  const route = getPublicRoute(source.pathname);
  const targetPath = route ? publicPath(route.routeId, locale) : publicPath('home', locale);
  return `${targetPath}${source.search}${source.hash}`;
}

interface NavigatePublicOptions {
  preserveScroll?: boolean;
  replace?: boolean;
}

export function navigatePublic(href: string, options: NavigatePublicOptions = {}) {
  const destination = new URL(href, window.location.origin);
  if (destination.href === window.location.href) {
    const target = destination.hash ? document.getElementById(destination.hash.slice(1)) : null;
    if (target) {
      target.scrollIntoView({ behavior: 'auto' });
      target.focus({ preventScroll: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      document.getElementById('main')?.focus({ preventScroll: true });
    }
    return;
  }
  const state = options.preserveScroll ? { ...window.history.state, klaraviewScrollY: window.scrollY } : {};
  const method = options.replace ? 'replaceState' : 'pushState';
  window.history[method](state, '', `${destination.pathname}${destination.search}${destination.hash}`);
  window.dispatchEvent(new PopStateEvent('popstate', { state }));
}

export function shouldHandleLink(event: Pick<React.MouseEvent, 'defaultPrevented' | 'button' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>, anchor: HTMLAnchorElement) {
  const url = new URL(anchor.href);
  return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey
    && !event.altKey && !event.shiftKey && !anchor.download && (!anchor.target || anchor.target === '_self')
    && url.origin === window.location.origin && !url.pathname.startsWith('/shared/')
    && !url.pathname.startsWith('/.auth/') && !url.pathname.startsWith('/downloads/');
}
